import { useCallback, useEffect, useState } from "react";
import { useCloseOnEscape } from "hooks/useCloseOnEscape";
import type { ProfilePanelTab } from "./profilePanel.contract";

type PendingEmploymentDiscardAction =
  | { readonly type: "cancelEdit" }
  | { readonly type: "closePanel" }
  | { readonly tab: ProfilePanelTab; readonly type: "changeTab" }
  | null;

interface UseUnsavedEmploymentGuardParams {
  readonly activeTab: ProfilePanelTab;
  readonly hasUnsavedChanges: boolean;
  readonly isCloseDisabled: boolean;
  readonly isDiscardConfirmOpen: boolean;
  readonly isOpen: boolean;
  readonly handleCancelEdit: () => void;
  readonly handleTabChange: (tab: ProfilePanelTab) => void;
  readonly onClose: () => void;
}

interface UseUnsavedEmploymentGuardResult {
  readonly isConfirmOpen: boolean;
  readonly cancelDiscard: () => void;
  readonly confirmDiscard: () => void;
  readonly requestCancelEdit: () => void;
  readonly requestPanelClose: () => void;
  readonly requestTabChange: (value: string) => void;
}

function isProfilePanelTab(value: string): value is ProfilePanelTab {
  return (
    value === "profile" ||
    value === "employmentSalary" ||
    value === "history" ||
    value === "agreements"
  );
}

function assertUnreachable(value: never): never {
  throw new Error(`Unhandled employment discard action: ${String(value)}`);
}

export const useUnsavedEmploymentGuard = (
  props: UseUnsavedEmploymentGuardParams,
): UseUnsavedEmploymentGuardResult => {
  const activeTab = props.activeTab;
  const hasUnsavedChanges = props.hasUnsavedChanges;
  const isCloseDisabled = props.isCloseDisabled;
  const isDiscardConfirmOpen = props.isDiscardConfirmOpen;
  const isOpen = props.isOpen;
  const handleCancelEdit = props.handleCancelEdit;
  const handleTabChange = props.handleTabChange;
  const onClose = props.onClose;
  const [pendingAction, setPendingAction] =
    useState<PendingEmploymentDiscardAction>(null);

  useEffect(() => {
    if (isOpen) return;
    setPendingAction(null);
  }, [isOpen]);

  const cancelDiscard = useCallback((): void => {
    setPendingAction(null);
  }, []);

  const closePanel = useCallback((): void => {
    setPendingAction(null);
    onClose();
  }, [onClose]);

  const requestPanelClose = useCallback((): void => {
    if (isCloseDisabled) return;
    if (hasUnsavedChanges) {
      setPendingAction({ type: "closePanel" });
      return;
    }
    closePanel();
  }, [closePanel, hasUnsavedChanges, isCloseDisabled]);

  const requestTabChange = useCallback(
    (value: string): void => {
      if (!isProfilePanelTab(value) || value === activeTab) return;
      if (hasUnsavedChanges) {
        setPendingAction({ tab: value, type: "changeTab" });
        return;
      }
      handleTabChange(value);
    },
    [activeTab, handleTabChange, hasUnsavedChanges],
  );

  const requestCancelEdit = useCallback((): void => {
    if (hasUnsavedChanges) {
      setPendingAction({ type: "cancelEdit" });
      return;
    }
    handleCancelEdit();
  }, [handleCancelEdit, hasUnsavedChanges]);

  const confirmDiscard = useCallback((): void => {
    if (pendingAction == null) return;
    setPendingAction(null);

    switch (pendingAction.type) {
      case "closePanel":
        onClose();
        return;
      case "cancelEdit":
        handleCancelEdit();
        return;
      case "changeTab":
        handleCancelEdit();
        handleTabChange(pendingAction.tab);
        return;
      default:
        assertUnreachable(pendingAction);
    }
  }, [handleCancelEdit, handleTabChange, onClose, pendingAction]);

  useEffect(() => {
    if (!isOpen || !hasUnsavedChanges) return;

    const handleBeforeUnload = (event: BeforeUnloadEvent): void => {
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges, isOpen]);

  useCloseOnEscape({
    isOpen: isOpen && !isDiscardConfirmOpen && pendingAction == null,
    onClose: requestPanelClose,
    stopPropagation: true,
    preventDefault: true,
  });

  return {
    isConfirmOpen: pendingAction != null,
    cancelDiscard,
    confirmDiscard,
    requestCancelEdit,
    requestPanelClose,
    requestTabChange,
  };
};
